import ErrorCodes, {CbError} from '@/hosted_fields/common/errors';
import t from '@/hosted_fields/common/locale';
import {RecaptchaOptions} from '@/plugins/captcha/types';
import RetryMechanism, {RetryStrategy} from '@/utils/retry-mechanism';

export default abstract class Captcha {
  protected CAPTCHA_LOAD_TIMEOUT: number = 30000;
  protected CAPTCHA_GENERATE_TOKEN_TIMEOUT: number = 10000;
  protected MAX_RETRIES_GENERATE_TOKEN = 3;
  protected MAX_RETRIES_INITIALIZE = 5;

  protected options: RecaptchaOptions;
  private script: HTMLScriptElement;
  private initializer: RetryMechanism<void>;

  constructor(options: RecaptchaOptions) {
    this.options = options;
  }

  protected getSiteKey(): string {
    return this.options.siteKey;
  }

  protected getSdkEndpoint(): string {
    return this.options.sdkEndpoint;
  }

  public initialize(): Promise<void> {
    if (!this.initializer) {
      this.initializer = new RetryMechanism(() => this.load(), {
        actionName: 'captcha_initialization',
        errors: {
          executionTimeout: t(ErrorCodes.recaptchaLoadTimeout),
          maxAttemptsReached: t(ErrorCodes.captchaInitializationMaxRetryOver),
        },
        strategy: RetryStrategy.ExecuteOnce,
        onError: this.options.onError,
      })
        .setExecutionTimeout(this.CAPTCHA_LOAD_TIMEOUT)
        .setMaxRetries(this.MAX_RETRIES_INITIALIZE);
    }
    return this.initializer.execute();
  }

  protected abstract load(): Promise<void>;
  public generateToken(action?: string): Promise<string | undefined> {
    return new RetryMechanism(
      async () => {
        await this.initialize();
        return await this.execute(action);
      },
      {
        actionName: 'captcha_generate_token',
        errors: {
          executionTimeout: t(ErrorCodes.recaptchaInitiationTimeout),
          maxAttemptsReached: t(ErrorCodes.captchaGenerateTokenMaxRetryOver),
        },
        onError: this.options.onError,
      }
    )
      .setExecutionTimeout(this.CAPTCHA_GENERATE_TOKEN_TIMEOUT)
      .setMaxRetries(this.MAX_RETRIES_GENERATE_TOKEN)
      .execute();
  }

  protected abstract execute(action?: string): Promise<string>;
  protected abstract getScriptURL(): string;

  protected loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Resolve if script is already loaded or site key is absent (site key can be absent when recaptcha is disabled)
      if (this.script || !this.getSiteKey()) return resolve();

      const script = document.createElement('script');
      script.src = this.getScriptURL();
      script.async = false;
      script.defer = false;

      script.onload = () => {
        this.script = script;
        resolve();
      };
      script.onerror = () => {
        script.remove();
        reject(new CbError(t(ErrorCodes.recaptchaScriptLoadFailed)));
      };
      document.head.appendChild(script);
    });
  }
}
